import type { Column } from "../types";

/**
 * The container order list's columns.
 *
 * The server owns the user's arrangement; this module owns what the list is
 * actually able to draw. Keeping the two apart is what makes stale settings
 * harmless: a saved column whose id is not in `CONTAINER_ORDER_COLUMN_IDS` has
 * no cell renderer, so it is filtered out of the header and the body together
 * rather than producing a header with no column under it.
 */

/**
 * Every column the list can render, in the order the page shipped with.
 *
 * Mirrors `defaultContainerOrderColumns` in the backend's settings controller —
 * the server is the source of truth, and this copy is only what the list falls
 * back to when the settings request has not landed (or failed).
 */
export const DEFAULT_CONTAINER_ORDER_COLUMNS: Column[] = [
  { id: "orderNumber", name: "Order", visible: true, fixed: true, order: 0 },
  {
    id: "referenceNumber",
    name: "Reference",
    visible: true,
    fixed: false,
    order: 1,
  },
  {
    id: "contractNumber",
    name: "Booking no.",
    visible: false,
    fixed: false,
    order: 2,
  },
  { id: "client", name: "Client", visible: true, fixed: false, order: 3 },
  {
    id: "createdBy",
    name: "Created by",
    visible: false,
    fixed: false,
    order: 4,
  },
  // Who is dealing with the booking — the column the Mine / Everyone filter is
  // read through. Visible by default: an office narrowing a list to one person
  // needs to see whose the rows are when they widen it again.
  { id: "handler", name: "Handler", visible: true, fixed: false, order: 5 },
  { id: "incoterm", name: "Incoterm", visible: false, fixed: false, order: 6 },
  { id: "containerType", name: "Type", visible: true, fixed: false, order: 7 },
  { id: "isoCode", name: "ISO code", visible: false, fixed: false, order: 8 },
  {
    id: "shippingLine",
    name: "Shipping line",
    visible: false,
    fixed: false,
    order: 9,
  },
  {
    id: "vessel",
    name: "Vessel / voyage",
    visible: false,
    fixed: false,
    order: 10,
  },
  { id: "handling", name: "Handling", visible: false, fixed: false, order: 11 },
  { id: "route", name: "Route", visible: true, fixed: false, order: 12 },
  {
    id: "loadingSite",
    name: "Loading site",
    visible: true,
    fixed: false,
    order: 13,
  },
  { id: "cutOff", name: "Cut-off", visible: true, fixed: false, order: 14 },
  { id: "validity", name: "Validity", visible: false, fixed: false, order: 15 },
  {
    id: "containers",
    name: "Containers",
    visible: true,
    fixed: false,
    order: 16,
  },
  {
    id: "documents",
    name: "Documents",
    visible: false,
    fixed: false,
    order: 21,
  },
  { id: "notes", name: "Notes", visible: false, fixed: false, order: 22 },
  { id: "createdAt", name: "Created", visible: false, fixed: false, order: 23 },
  { id: "updatedAt", name: "Updated", visible: false, fixed: false, order: 24 },
  { id: "actions", name: "Actions", visible: true, fixed: false, order: 25 },
];

/** Ids the list has a cell renderer for. */
export const CONTAINER_ORDER_COLUMN_IDS = new Set(
  DEFAULT_CONTAINER_ORDER_COLUMNS.map((column) => column.id),
);

/**
 * Translation key for a column's header, under the `containerOrders` namespace.
 *
 * The ids are the same ones `list.columns.*` was already keyed by, so the
 * headers keep their existing translations in both languages. The server's
 * `name` is only a fallback for a key that has not been added yet.
 */
export const containerOrderColumnLabelKey = (columnId: string): string =>
  `list.columns.${columnId}`;

/**
 * The columns to draw, from whatever the settings request produced.
 *
 * Handles the three states a caller can be in — nothing loaded yet, settings
 * that predate the columns the app now renders, and a saved arrangement where
 * everything has been switched off — and always returns something drawable, so
 * the table can never come out as a row of empty cells.
 */
export const resolveContainerOrderColumns = (columns: Column[]): Column[] => {
  const known = (columns ?? []).filter((column) =>
    CONTAINER_ORDER_COLUMN_IDS.has(column.id),
  );

  return known.length ? known : DEFAULT_CONTAINER_ORDER_COLUMNS;
};

/**
 * The visible subset, guarded against an empty table.
 *
 * The settings dropdown refuses to switch off the last column and the server
 * refuses to store such a payload, but settings written before either guard
 * existed would still render a table with nothing but the checkbox in it.
 */
export const visibleContainerOrderColumns = (columns: Column[]): Column[] => {
  const resolved = resolveContainerOrderColumns(columns);
  const visible = resolved.filter((column) => column.visible);

  return visible.length
    ? visible
    : resolved.filter((column) => column.fixed).length
      ? resolved.filter((column) => column.fixed)
      : resolved.slice(0, 1);
};
